import { OSM_API, OSM_SITE, OSM_WIKI } from "./config.ts";

export type OsmType = "node" | "way" | "relation";

export interface Ref {
  type: OsmType;
  id: number;
}

export interface OsmMember {
  type: OsmType;
  ref: number;
  role: string;
}

export interface OsmElement {
  type: OsmType;
  id: number;
  version: number;
  timestamp: string;
  changeset: number;
  user?: string;
  lat?: number;
  lon?: number;
  nodes?: number[];
  members?: OsmMember[];
  tags?: Record<string, string>;
}

export function refsEqual(a: Ref | null, b: Ref | null): boolean {
  if (!a || !b) return a === b;
  return a.type === b.type && a.id === b.id;
}

const cache = new Map<string, Promise<unknown>>();

function getJson(url: string): Promise<unknown> {
  const cached = cache.get(url);
  if (cached) return cached;

  const pending = fetch(url).then((response) => {
    if (!response.ok) throw new Error(`${response.status} ${response.statusText} fetching ${url}`);
    return response.json();
  });
  pending.catch(() => cache.delete(url));
  cache.set(url, pending);
  return pending;
}

interface ElementsResponse {
  elements: OsmElement[];
}

/** An element together with everything the `/full` response returned with it. */
export interface FullElement {
  element: OsmElement;
  /** Keyed by `type[0] + id`. */
  members: Map<string, OsmElement>;
}

const key = (type: OsmType, id: number) => type.charAt(0) + id;

export async function getElement(type: OsmType, id: number): Promise<FullElement> {
  const url = `${OSM_API}/${type}/${id}${type === "node" ? "" : "/full"}.json`;
  const { elements } = (await getJson(url)) as ElementsResponse;

  const members = new Map(elements.map((el) => [key(el.type, el.id), el]));
  const element = members.get(key(type, id));
  if (!element) throw new Error(`${type}/${id} missing from its own API response`);
  return { element, members };
}

export const elementUrl = ({ type, id }: Ref) => `${OSM_SITE}/${type}/${id}`;
export const historyUrl = (ref: Ref) => `${elementUrl(ref)}/history`;
export const userUrl = (user: string) => `${OSM_SITE}/user/${encodeURIComponent(user)}`;
export const changesetUrl = (id: number) => `${OSM_SITE}/changeset/${id}`;
export const osmchaUrl = (id: number) => `https://osmcha.org/changesets/${id}`;
export const idEditorUrl = ({ type, id }: Ref) => `${OSM_SITE}/edit?editor=id&${type}=${id}`;
export const josmUrl = ({ type, id }: Ref) => `${OSM_SITE}/edit?editor=remote&${type}=${id}`;
export const keyWikiUrl = (key: string) => `${OSM_WIKI}/Key:${encodeURIComponent(key)}`;

const URL_RE = /^https?:\/\/[^\s]+$/i;
const QID_RE = /^Q\d+$/;
const WIKIPEDIA_RE = /^([a-z-]+):(.+)$/i;

/** The page a tag's value should link to, if any. */
export function valueUrl(key: string, value: string): string | null {
  if (URL_RE.test(value)) return value;

  if ((key === "wikidata" || key.endsWith(":wikidata")) && QID_RE.test(value)) {
    return `https://www.wikidata.org/wiki/${value}`;
  }

  if (key === "wikipedia" || key.endsWith(":wikipedia")) {
    const match = WIKIPEDIA_RE.exec(value);
    if (match) {
      const [, lang, title] = match as unknown as [string, string, string];
      return `https://${lang}.wikipedia.org/wiki/${encodeURIComponent(title.replace(/ /g, "_"))}`;
    }
  }

  return null;
}
